import {
  getBundleDefinition,
  isBundleEntitlementKey,
} from "@/features/payment/paymentDefinitions";
import { summarizePermanentPurchaseEffects } from "@/features/payment/paymentPermanentEffects";
import {
  DEFAULT_ENTITLEMENTS,
  type BundleEntitlementKey,
  type EntitlementKey,
  type EntitlementSnapshot,
  type HoverIdlePaymentState,
} from "@/features/payment/types";

const SCORE_INCOME_BONUS_MULTIPLIER = 1.25;
const OFFLINE_PROGRESS_BONUS_MULTIPLIER = 1.5;

type MutableEntitlementSnapshot = {
  -readonly [K in keyof EntitlementSnapshot]: EntitlementSnapshot[K];
};

export function cloneEntitlementSnapshot(
  snapshot: EntitlementSnapshot,
): EntitlementSnapshot {
  return {
    scoreIncomeMultiplier: snapshot.scoreIncomeMultiplier,
    hoverIncomeMultiplier: snapshot.hoverIncomeMultiplier,
    timIncomeMultiplier: snapshot.timIncomeMultiplier,
    offlineProgressMultiplier: snapshot.offlineProgressMultiplier,
    hasSupporterPack: snapshot.hasSupporterPack,
  };
}

export function createEntitlementSnapshotFromKeys(
  keys: readonly EntitlementKey[],
): EntitlementSnapshot {
  const snapshot: MutableEntitlementSnapshot = {
    ...DEFAULT_ENTITLEMENTS,
  };

  for (const key of keys) {
    if (key === "score_income_bonus") {
      snapshot.scoreIncomeMultiplier *= SCORE_INCOME_BONUS_MULTIPLIER;
      continue;
    }
    if (key === "offline_progress_bonus") {
      snapshot.offlineProgressMultiplier *= OFFLINE_PROGRESS_BONUS_MULTIPLIER;
      continue;
    }
    snapshot.hasSupporterPack = true;
  }

  return snapshot;
}

export function createEntitlementSnapshotFromBundleKeys(
  bundleKeys: readonly BundleEntitlementKey[],
): EntitlementSnapshot {
  const seen = new Set<BundleEntitlementKey>();
  const normalizedKeys: EntitlementKey[] = [];
  for (const key of bundleKeys) {
    if (!isBundleEntitlementKey(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    const definition = getBundleDefinition(key);
    for (const entitlementKey of definition.entitlementKeys) {
      normalizedKeys.push(entitlementKey);
    }
  }
  return createEntitlementSnapshotFromKeys(normalizedKeys);
}

export function createEntitlementSnapshotFromPaymentState(
  paymentState: Pick<HoverIdlePaymentState, "purchasedPermanentItems">,
): EntitlementSnapshot {
  const summary = summarizePermanentPurchaseEffects(
    paymentState.purchasedPermanentItems,
  );

  return {
    ...DEFAULT_ENTITLEMENTS,
    hoverIncomeMultiplier: 1 + summary.hovGainPercent / 100,
    timIncomeMultiplier: 1 + summary.timGainPercent / 100,
  };
}

export function mergeEntitlementSnapshots(
  ...snapshots: readonly EntitlementSnapshot[]
): EntitlementSnapshot {
  const merged: MutableEntitlementSnapshot = {
    ...DEFAULT_ENTITLEMENTS,
  };

  for (const snapshot of snapshots) {
    merged.scoreIncomeMultiplier *= snapshot.scoreIncomeMultiplier;
    merged.hoverIncomeMultiplier *= snapshot.hoverIncomeMultiplier;
    merged.timIncomeMultiplier *= snapshot.timIncomeMultiplier;
    merged.offlineProgressMultiplier *= snapshot.offlineProgressMultiplier;
    merged.hasSupporterPack =
      merged.hasSupporterPack || snapshot.hasSupporterPack;
  }

  return merged;
}
